import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe } from '../api/auth.api';

const AuthContext = createContext(null);

const ADMIN_TOKEN_KEY = 'drusshti_admin_token'; // stores original admin token during impersonation
const TOKEN_KEY       = 'drusshti_token';
const ROLE_KEY        = 'drusshti_role';

export const AuthProvider = ({ children }) => {
  const [user,            setUser]            = useState(null);
  const [role,            setRole]            = useState(null);
  const [token,           setToken]           = useState(localStorage.getItem(TOKEN_KEY));
  const [isLoading,       setIsLoading]       = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (!stored) { setIsLoading(false); return; }
      try {
        const res = await getMe();
        setUser(res.data.user);
        setRole(res.data.role);
        setIsImpersonating(res.data.impersonating || false);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ROLE_KEY);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    };
    bootstrap();
  }, []);

  const login = useCallback((responseData) => {
    const { token: t, user: u, role: r } = responseData;
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(ROLE_KEY,  r);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken(t); setUser(u); setRole(r); setIsImpersonating(false);
  }, []);

  // Save original admin token, then switch to impersonation token
  const startImpersonation = useCallback((responseData) => {
    const { token: impToken, impersonatedUser } = responseData;
    // Save current admin token so we can restore it on exit
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
    localStorage.setItem(TOKEN_KEY, impToken);
    setToken(impToken);
    setUser(impersonatedUser);
    setIsImpersonating(true);
  }, []);

  // Exit impersonation — restore original admin token
  const stopImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (adminToken) {
      localStorage.setItem(TOKEN_KEY, adminToken);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setToken(adminToken);
      setIsImpersonating(false);
      // Re-fetch admin profile with restored token
      getMe().then(res => {
        setUser(res.data.user);
        setRole(res.data.role);
      }).catch(() => {
        // If restore fails, do full logout
        localStorage.removeItem(TOKEN_KEY);
        setToken(null); setUser(null); setRole(null);
      });
    } else {
      // No saved admin token — full logout
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
      setToken(null); setUser(null); setRole(null); setIsImpersonating(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken(null); setUser(null); setRole(null); setIsImpersonating(false);
  }, []);

  const isAdmin = role === 'admin' && !isImpersonating;

  return (
    <AuthContext.Provider value={{
      user, role, token, isLoading, isAdmin, isImpersonating,
      login, logout, startImpersonation, stopImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
